import { reduceObject } from '../../utility'
import {
	getDeathStatusFromMission,
	getStudent,
} from '@mission.io/mission-toolkit'
import {
	MISSION_PHASES,
	COLLABORATIVE_CULMINATING_MOMENT,
} from '@mission.io/mission-toolkit/constants'
import type {
	DisplayTeam,
	JuniorPlusMissionFullState,
	JuniorMissionFullState,
	BothFullMissionStates,
	MapInfo,
	Timer,
	StudentWithPoints,
	Map,
	CrewDeathStatus,
	JuniorTeam,
	GivenMapPointInformation,
} from '@mission.io/mission-toolkit'
import {
	getBasicPointsForStudent,
	getShipHealthFromMission,
	getMainObjectiveFromMission,
	getIsPausedFromMission,
	getIsMissionPauseActiveFromMission,
	getMapInfoFromMission,
	isJrPlusCommunicationActive,
	isJrPlusDefenseActive,
	isJrPlusTractorBeamActive,
	isJrPlusThrustersActive,
	isJrPlusTransporterActive,
	isJrPlusPowerActive,
	isJrPowerActive,
	isJrScannerActive,
	isJrTractorBeamActive,
	isJrTransporterActive,
	isJrDefenseActive,
	isJrCommunicationActive,
	isJrThrustersActive,
	getMapFromMission,
	getIsDeathHappeningFromMission,
	getTimersFromMission,
	getIsTrainingFromMission,
	getPhaseFromMission,
	getTeamsFromMission,
	getConnectedStudents as getConnectedStudentsFromMission,
	getScreenFromMission as getScreen,
} from '@mission.io/mission-toolkit'

type State = ?BothFullMissionStates

export function getTotalPoints(missionState: State): number {
	return missionState
		? reduceObject(
				missionState.points.studentPoints,
				(total, _, studentId) => {
					const student = getStudent(missionState, studentId)
					if (student?.isOwner) {
						return total
					}
					return total + getBasicPointsForStudent(missionState, studentId)
				},
				0
		  )
		: 0
}

/**
 * getTotalCrewPoints - get the total amount of crew points from the mission state. THIS IS NOT
 * MULTIPLIED BY THE STUDENT COUNT.
 *
 * @param  {State} missionState - the current mission state
 * @returns number
 */
export function getTotalCrewPoints(missionState: State): number {
	return missionState?.points?.crewPoints
		? reduceObject(
				missionState.points.crewPoints,
				(total, _, category) => {
					return total + missionState.points.crewPoints[category]
				},
				0
		  )
		: 0
}

/**
 * getMapPointInformation - get the map scan points information for all maps
 *
 * @param {?JuniorPlusMissionFullState} missionState - the state to get the information from
 *
 * @return {{[mapId: string]: GivenMapPointInformation}}
 */
export function getMapPointInformation(
	missionState: ?JuniorPlusMissionFullState
): {
	[mapId: string]: GivenMapPointInformation,
} {
	const result = {}

	if (!missionState) {
		return result
	}

	Object.keys(missionState.mapInfo).forEach(mapId => {
		const pointInformation = missionState.mapInfo[mapId].givenPointsInformation
		if (!pointInformation) {
			return
		}
		result[mapId] = pointInformation
	})

	return result
}

export function getShipHealth(missionState: State): number {
	return getShipHealthFromMission(missionState)
}

export function getMainObjective(state: State): {
	objectiveText: string,
	teacherInfo: string,
} {
	return getMainObjectiveFromMission(state)
}

export function isPaused(state: State): boolean {
	return getIsPausedFromMission(state)
}

export function isMissionPauseActive(state: State): boolean {
	return getIsMissionPauseActiveFromMission(state)
}

export function isDeathHappening(state: State): boolean {
	return getIsDeathHappeningFromMission(state)
}

export function getDeathState(state: State): ?CrewDeathStatus {
	return getDeathStatusFromMission(state)
}

export function isTraining(state: State): boolean {
	return getIsTrainingFromMission(state)
}
export function getMissionPhase(state: State): ?$Keys<typeof MISSION_PHASES> {
	return getPhaseFromMission(state)
}
export function isLogin(state: State): boolean {
	return getPhaseFromMission(state) === MISSION_PHASES.LOGIN
}

export function getMap(state: ?JuniorPlusMissionFullState): ?Map {
	return getMapFromMission(state)
}

export function getMapInfo(state: ?JuniorPlusMissionFullState): ?MapInfo {
	return getMapInfoFromMission(state)
}

export function getNavigationMapData(state: State): ?mixed {
	return state?.navigation.data
}

export function getTeams(state: ?JuniorPlusMissionFullState): ?(DisplayTeam[]) {
	return getTeamsFromMission(state)
}

export const getTimers: (
	timerIds?: ?Array<?string>
) => (state: State) => { [string]: Timer } =
	(timerIds?: ?Array<?string>) =>
	(state: State): { [string]: Timer } => {
		return getTimersFromMission(timerIds)(state)
	}

export const DEFAULT_PLUS_STATION_ACTIVITY = {
	communicationPlus: false,
	transporterPlus: false,
	defensePlus: false,
	tractorBeamPlus: false,
	thrustersPlus: false,
	powerPlus: false,
}

export function getPlusStationActivity(
	missionState: ?JuniorPlusMissionFullState
): {
	[string]: boolean,
} {
	return {
		communicationPlus: isJrPlusCommunicationActive(missionState),
		transporterPlus: isJrPlusTransporterActive(missionState),
		defensePlus: isJrPlusDefenseActive(missionState),
		tractorBeamPlus: isJrPlusTractorBeamActive(missionState),
		thrustersPlus: isJrPlusThrustersActive(missionState),
		powerPlus: isJrPlusPowerActive(missionState),
	}
}

export const DEFAULT_JR_STATION_ACTIVITY = {
	communication: false,
	scanner: false,
	transporter: false,
	defense: false,
	tractorBeam: false,
	thrusters: false,
	power: false,
}

export function getJrStationActivity(missionState: ?JuniorMissionFullState): {
	[string]: boolean,
} {
	return {
		communication: isJrCommunicationActive(missionState),
		scanner: isJrScannerActive(missionState),
		transporter: isJrTransporterActive(missionState),
		defense: isJrDefenseActive(missionState),
		tractorBeam: isJrTractorBeamActive(missionState),
		thrusters: isJrThrustersActive(missionState),
		power: isJrPowerActive(missionState),
	}
}

/**
 * Gets connected students from a junior mission.
 * @param {StoreState} state
 * @return IdMap of JuniorSharedStudents
 */
export function getConnectedStudents(state: State): {
	[string]: StudentWithPoints,
} {
	return getConnectedStudentsFromMission(state)
}

/**
 *  Get the next document in the queue that will appear when the creative canvas ending is set to EACH_RESPONSE
 * @return {{documentId: string, team: JuniorTeam}} the documentId and team that owns the document that will be shown next
 */
export function getNextDocumentToShow(
	state: ?JuniorPlusMissionFullState
): ?{ documentId: string, team: JuniorTeam } {
	const screen = getScreen(state)
	// Handle edge cases where the selector is irrelevant
	if (
		!state ||
		!screen ||
		screen.type !== 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN' ||
		screen.currentStep.status !==
			COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS ||
		screen.ending !== COLLABORATIVE_CULMINATING_MOMENT.ENDING.EACH_RESPONSE
	) {
		return
	}

	const queue = screen.currentStep.queue
	const nextDocument = [...queue].pop()
	if (!nextDocument) {
		return
	}
	const nextDocumentId = nextDocument.documentResultsToShow[0]
	const currentCanvasId = state.stationData.creativeCanvas.currentCanvasId
	if (!currentCanvasId) {
		return
	}
	const teamId = state.stationData.creativeCanvas.canvases[
		currentCanvasId
	].teams.find(team => team.document.id === nextDocumentId)?.teamId
	const team = teamId ? state.teams[teamId] : null
	if (!team) {
		return
	}
	return {
		team,
		documentId: nextDocumentId,
	}
}
